import { QuestionnaireResponse } from '../dtos/questionnaire';
import { PatientResponse, UserData } from '../dtos/patient';

export type RedirectPlatform = 'ios' | 'android' | 'other';



export function getStoreUrl(platform: RedirectPlatform): string | undefined {
  if (platform === 'ios') {
    return process.env.REACT_APP_APP_STORE_URL;
  }
  if (platform === 'android') {
    return process.env.REACT_APP_PLAY_STORE_URL;
  }
  return undefined;
}

/** Sanitize env vars of stray whitespace and '=' characters. */
const sanitize = (val: string | undefined): string =>
  (val || "").trim().replace(/^=/, "").replace(/=+$/, "");

export interface PlatformInfo {
  isIOS: boolean;
  isAndroid: boolean;
  deepLink: string;
  storeUrl: string;
  androidIntent: string;
}

/** Detect platform, build deep link and store URLs for app redirect. */
export function getPlatformInfo(): PlatformInfo {
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  const isAndroid = /android/i.test(userAgent);

  const baseDeepLink = sanitize(process.env.REACT_APP_APP_DEEP_LINK);
  const storeUrl = isIOS
    ? sanitize(process.env.REACT_APP_APP_STORE_URL)
    : sanitize(process.env.REACT_APP_PLAY_STORE_URL);

  const queryString = window.location.search;
  const deepLink = baseDeepLink ? baseDeepLink + (queryString || "") : "";

  let androidIntent = "";
  if (isAndroid && baseDeepLink && storeUrl) {
    const schemeBase = baseDeepLink.split("://")[0];
    const hostAndPath = baseDeepLink.split("://")[1] || "";
    const hostWithParams = hostAndPath + (queryString || "");
    const packageId = "com.vctors.mtapp.case_managment";
    androidIntent = `intent://${hostWithParams}#Intent;scheme=${schemeBase};package=${packageId};S.browser_fallback_url=${encodeURIComponent(storeUrl)};end`;
  }

  return { isIOS, isAndroid, deepLink, storeUrl, androidIntent };
}

/**
 * Display & Formatting Utilities
 */
export const formatUserName = (name: string): string => {
  if (!name) return 'User';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase().split(' ')[0];
};

export const getUserInitials = (name: string): string => {
  if (!name) return 'U';
  return name.charAt(0).toUpperCase();
};

export const formatAge = (age: number): string => {
  if (!age) return 'Unknown age';
  return `${age} years`;
};

export const formatGender = (gender: string): string => {
  if (!gender) return 'Unknown';
  return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
};

/** Convert Western numerals to Arabic numerals (for RTL). */
export function convertToArabicNumerals(
  number: number | string | null | undefined
): string {
  if (number == null || number === '') return '';
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return String(number).replace(
    /[0-9]/g,
    (digit) => arabicNumerals[parseInt(digit, 10)]
  );
}

export interface GetGenderAgeDisplayParams {
  gender: string | null | undefined;
  age: number | string | null | undefined;
  labels: { female: string; male: string; years: string };
  isRTL?: boolean;
  emptyPlaceholder?: string;
}

/** Build a single display line for gender and age (e.g. "Female, 35 years" or "—"). */
export function getGenderAgeDisplay(params: GetGenderAgeDisplayParams): string {
  const {
    gender,
    age,
    labels,
    isRTL = false,
    emptyPlaceholder = '—',
  } = params;

  const genderStr =
    gender == null
      ? ''
      : gender.toLowerCase() === 'female'
        ? labels.female
        : labels.male;

  const ageStr =
    age == null || age === ''
      ? ''
      : isRTL
        ? convertToArabicNumerals(age)
        : String(age);

  const parts = [genderStr, ageStr].filter(Boolean);
  const base = parts.length ? parts.join(', ') : emptyPlaceholder;
  const yearsSuffix = age != null && age !== '' ? ` ${labels.years}` : '';
  return base + yearsSuffix;
}

/** 
 * Calculation & Logic Utilities
 */
export function calculateAge(dateOfBirth: string | Date | null | undefined): number | null {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/** Schedule entry from pod schedule API */
interface ScheduleEntry {
  dayOfWeek?: string;
  startTime?: string;
  endTime?: string;
}

/** Schedule data from fetchPodSchedule */
interface ScheduleData {
  timezone?: string;
  entries?: ScheduleEntry[];
}

/**
 * Check if current time is within available pod schedule slots.
 */
export function isPodAvailable(scheduleData: ScheduleData | null | undefined): boolean {
  if (!scheduleData?.entries?.length) {
    return false;
  }

  try {
    const now = new Date();
    const timezone = scheduleData.timezone ?? 'UTC';

    const dayFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'long',
    });
    const currentDayOfWeek = dayFormatter.format(now).toUpperCase();

    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const timeParts = timeFormatter.formatToParts(now);
    const hour = timeParts.find((p) => p.type === 'hour')?.value.padStart(2, '0') ?? '00';
    const minute = timeParts.find((p) => p.type === 'minute')?.value.padStart(2, '0') ?? '00';
    const currentTime = `${hour}:${minute}`;

    for (const entry of scheduleData.entries) {
      if (entry.dayOfWeek?.toUpperCase() === currentDayOfWeek && entry.startTime && entry.endTime) {
        if (currentTime >= entry.startTime && currentTime < entry.endTime) {
          return true;
        }
      }
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Mapping Utilities
 */
export const mapQuestionnaireResponseToCategory = (
  response: QuestionnaireResponse[] | QuestionnaireResponse,
  type: string
) => {
  const data = Array.isArray(response) ? response[0] : response;
  if (!data) return null;

  return {
    id: data.id,
    name: type,
    title: data.name.replace("Assessment", "").trim(),
    questions: data.questions || [],
    questionCount: data.questions?.length || 0,
  };
};

export const mapPatientToUserData = (patient: PatientResponse, sessionId: string | number, podIp: string): UserData => ({
  id: patient.user_id,
  patientId: patient.patient_id,
  sessionId: String(sessionId),
  podIP: podIp,
  name: patient.name ?? '',
  gender: patient.gender ?? '',
  dateOfBirth: patient.date_of_birth ?? null,
  age: patient.date_of_birth ? calculateAge(patient.date_of_birth) : null,
});

/**
 * General Purpose Utilities
 */
export const generateRandomProgress = (min = 0, max = 100): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const validateUserData = (userData: any): boolean => {
  if (!userData) return false;
  const hasRequiredFields = !!(userData.name && userData.age);
  return hasRequiredFields;
};

/**
 * Parse an ISO date string as UTC if it has no timezone indicator.
 * Servers often return timestamps without Z or offset (e.g. "2026-02-25T02:12:06.50504").
 * JavaScript would parse these as local time, which breaks across timezones.
 */
export function parseAsUTC(dateStr: string): number {
  const hasTimezone = /Z|[+-]\d{2}:\d{2}$/.test(dateStr);
  return new Date(hasTimezone ? dateStr : `${dateStr}Z`).getTime();
}

export const getCurrentTime = (): string => {
  const now = new Date();
  return now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format duration in seconds to MM:SS
 */
export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Service Token Storage Helpers
 * Persist the service account token in sessionStorage between page refreshes.
 */
const SERVICE_TOKEN_KEY = "service_account_token";
const SERVICE_TOKEN_EXPIRES_AT_KEY = "service_account_token_expires_at";
export const EXPIRY_BUFFER_MS = 60 * 1000; // refresh 1 min before expiry

export function getStoredServiceToken(): string | null {
  const token = sessionStorage.getItem(SERVICE_TOKEN_KEY);
  const expiresAt = sessionStorage.getItem(SERVICE_TOKEN_EXPIRES_AT_KEY) ?? 0;
  if (!token || !expiresAt) return null;
  const expiresAtMs = parseInt(expiresAt as string, 10);
  if (Date.now() >= expiresAtMs - EXPIRY_BUFFER_MS) {
    clearStoredServiceToken();
    return null;
  }
  return token;
}

export function setStoredServiceToken(token: string, expiresInSeconds: number): void {
  const expiresAt = Date.now() + expiresInSeconds * 1000;
  sessionStorage.setItem(SERVICE_TOKEN_KEY, token);
  sessionStorage.setItem(SERVICE_TOKEN_EXPIRES_AT_KEY, String(expiresAt));
}

export function clearStoredServiceToken(): void {
  sessionStorage.removeItem(SERVICE_TOKEN_KEY);
  sessionStorage.removeItem(SERVICE_TOKEN_EXPIRES_AT_KEY);
}

export function getRemainingTokenTime(): number {
  const storedExpiresAt = parseInt(sessionStorage.getItem(SERVICE_TOKEN_EXPIRES_AT_KEY) ?? "0", 10);
  const remainingMs = storedExpiresAt - Date.now();
  return Math.max(Math.floor(remainingMs / 1000), 0);
}

/**
 * Pod Route & IP Utilities
 */

/** Check if a string is a valid IPv4 address. */
export const isPodIP = (ip: string | null | undefined): boolean => {
  if (!ip) return false;
  return /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(ip);
};

/** Extract the IP prefix from a URL pathname. */
export const getUrlPrefix = (pathname: string): string | null => {
  if (!pathname) return null;
  const segments = pathname.split('/').filter(Boolean);
  const potentialIp = segments[0];
  return isPodIP(potentialIp) ? potentialIp : null;
};

/** Get the saved Pod IP from tab-specific storage. */
export const getSavedPodIP = (): string | null => sessionStorage.getItem('current_pod_ip');

/**
 * Extracts an IPv4 address from a given URL string.
 * @param {string} url - The URL containing the IP address.
 * @returns {string|null} - The IP address if found, otherwise null.
 */
export const extractIPFromURL = (url) => {
  // Regex breakdown:
  // \b matches a word boundary
  // (?:[0-9]{1,3}\.){3} matches three sets of 1-3 digits followed by a dot
  // [0-9]{1,3} matches the final set of 1-3 digits
  const ipRegex = /\b(?:[0-9]{1,3}\.){3}[0-9]{1,3}\b/;

  const match = url.match(ipRegex);

  return match ? match[0] : null;
}
/** Save the Pod IP to tab-specific storage. */
export const savePodIP = (ip: string): void => sessionStorage.setItem('current_pod_ip', ip);

export const getUnprefixedPath = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length <= 1) return '/';
  return `/${segments.slice(1).join('/')}`;
};